/**
 * 需要想定 daily 画面の過去日 write-gate predicate。
 *
 * conventions.md §GC ロック「過去日は全ての値変更操作を封鎖する」
 * (principles.md 鉄則 1「表示と実行の乖離は許容しない」) の判定を単一の純関数へ
 * 集約し、write 入口 (編集 hook / orchestration 入口) と render の disabled /
 * readOnly の両方が同じ predicate 経由でロックを決めるようにする。
 *
 * 過去日判定の低レベル primitive は `~/models/dateTime` の
 * `isTargetDateBeforeTodayInJst` (JST 日境界) をそのまま使う。`now` を差し込める
 * signature は同 primitive と揃え、write 入口 (実時刻から再判定して no-op) と
 * render 時計算 (親の再レンダー時刻) の両方に流用できるようにする — 画面を開いた
 * まま JST 0:00 を跨いだ後の残存経路は write 入口が実時刻で再判定する規律
 * (`useForecastDraftEditor.ts` / `useJstMidnightTick.ts` 参照)。
 */
import { now as serverNow } from '~/models/clock'
import { isTargetDateBeforeTodayInJst } from '~/models/dateTime'

/**
 * targetDate に対する値変更 (書込 / 保存 / 確定 / 取込) を許可するか。
 * 過去日でない (= 未来日・当日・未取得) のとき true を返す。
 * 未取得 (`targetDate === undefined`) は下流の各入口で早期 return される
 * 前提のため true を返す (下流の areaCode / caseId 未解決 gate に委ねる。
 * `isTargetDateBeforeTodayInJst(undefined)` が false を返す既存挙動に一致)。
 */
export function canMutate(
  targetDate: { year: number; month: number; day: number } | undefined,
  now: Date = serverNow(),
): boolean {
  return !isTargetDateBeforeTodayInJst(targetDate, now)
}
